import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getPlatformLocations, getUser } from "api/methods";

import { useSwitchPlatformLocation } from "../useSwitchPlatformLocation";

type ListingNotAvailableHereProps = {
  /** Locations that sell the item. Empty means every location does. */
  readonly availableLocationIds: string[];
  readonly availableAtAllLocations: boolean;
};

/**
 * Landing screen for a shared link the patient's own location does not sell.
 *
 * Catalog reads are location-scoped, so the item resolves to a not-found and the patient is
 * dropped back to the shop with no idea why. This names the locations that do sell it and
 * switches them over, which is the only action that actually makes the link work.
 */
export const ListingNotAvailableHere = ({
  availableLocationIds,
  availableAtAllLocations,
}: ListingNotAvailableHereProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { switchLocation, isSwitching } = useSwitchPlatformLocation();
  const [failed, setFailed] = useState(false);

  const { data: user } = useQuery({ ...getUser.keyed() });
  const { data: locations } = useQuery({
    ...getPlatformLocations.keyed({}),
    select: ({ items }) => items,
  });

  const currentLocationName = user?.platformLocation?.name;
  const sellingLocations = (locations ?? []).filter((location) =>
    availableAtAllLocations ? true : availableLocationIds.includes(location.id),
  );

  const handleSwitch = async (platformLocationId: string) => {
    setFailed(false);
    if (!(await switchLocation(platformLocationId))) setFailed(true);
  };

  return (
    <div className="flex flex-col gap-4 px-6 py-8" data-testid="listing-not-available-here">
      <h1 className="text-bold1 text-primary">{t("listingNotAvailable.title")}</h1>

      <p className="text-body2 text-secondary">
        {currentLocationName
          ? t("listingNotAvailable.bodyWithLocation", { locationName: currentLocationName })
          : t("listingNotAvailable.body")}
      </p>

      {sellingLocations.length > 0 && (
        <>
          <p className="text-bold3 text-primary">{t("listingNotAvailable.availableAt")}</p>
          <ul className="flex flex-col gap-2">
            {sellingLocations.map((location) => (
              <li key={location.id}>
                <button
                  className="w-full rounded border border-light-grey px-4 py-3 text-left text-body2 text-primary disabled:opacity-50"
                  disabled={isSwitching}
                  type="button"
                  onClick={() => handleSwitch(location.id)}
                >
                  <span className="text-bold3">{location.name}</span>
                  <span className="block text-body3 text-secondary">
                    {t("listingNotAvailable.switchTo")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {failed && <p className="text-error text-body3">{t("listingNotAvailable.switchFailed")}</p>}

      <button
        className="text-body2 text-secondary underline"
        type="button"
        onClick={() => navigate("/shop")}
      >
        {t("listingNotAvailable.backToShop")}
      </button>
    </div>
  );
};
